import { cloneDeep } from "lodash/fp";

import { getUiSchemaList } from "../../services/applications";
import { updateSchemas } from "../../services/schemas";
import { Application } from "../../store";
import { getSchemas } from "../../services/schemas";
import { User } from "../../store/types";

interface SaveSchema {
	activeApplication: Application | null;
	activeMarket: string | null;
	value: any;
	setSchemaData: (data: any) => void;
	getToken: () => Promise<string>;
	handleLogout: () => void;
	setOriginalValue: (data: any) => void;
	setSaveBtnLoading: (value: boolean) => void;
	setToastMessage: (value: string) => void;
	uiSchemaName?: string;
	multipleMarketFlag: boolean;
	selectedMarkets: (string | null)[];
	reason?: string;
	currentUser?: User | null;
	setUnsavedChangesValue: (data: any) => void;
}

interface GetUiSchema {
	activeApplication: Application | null;
	getToken: () => Promise<string>;
	setUiSchemasLoading: (value: boolean) => void;
	setUiSchemasData: (data: any) => void;
	handleLogout: () => void;
}

export type JsonSchema = {
	type?: "object" | "array" | "string" | "number" | "integer" | "boolean" | "null";
	default?: any;
	properties?: Record<string, JsonSchema>;
	items?: JsonSchema;
};

export type UISchemaElement = {
	type?: string;
	scope?: string;
	elements?: UISchemaElement[];
	options?: {
		detail?: UISchemaElement;
		[key: string]: any;
	};
	[key: string]: any;
};

type UISchemaScopeSet = Set<string>;

export const getUiSchemas = async ({
	activeApplication,
	getToken,
	setUiSchemasLoading,
	setUiSchemasData,
	handleLogout,
}: GetUiSchema) => {
	try {
		if (activeApplication) {
			setUiSchemasLoading(true);
			const token = await getToken();
			const authHeader = {
				headers: {
					authorization: `Bearer ${token}`,
				},
			};
			const { data: UiSchemaData } = await getUiSchemaList(authHeader, activeApplication.id, {
				uiSchema: true,
			});
			const hasUiSchemas = UiSchemaData.data["ui-schemas"].length;
			setUiSchemasData(hasUiSchemas ? UiSchemaData.data["ui-schemas"] : undefined);
		}
	} catch (error: any) {
		//TODO: handle error
		console.error(error);
		setUiSchemasData(undefined);
		if (error?.response?.status == 401) {
			handleLogout();
		}
	} finally {
		setUiSchemasLoading(false);
	}
};

export const saveSchemaValue = async ({
	activeApplication,
	activeMarket,
	value,
	setSchemaData,
	handleLogout,
	setOriginalValue,
	getToken,
	uiSchemaName,
	setSaveBtnLoading,
	setToastMessage,
	multipleMarketFlag,
	selectedMarkets,
	reason,
	currentUser,
	setUnsavedChangesValue,
}: SaveSchema) => {
	if (activeApplication && activeMarket) {
		try {
			setSaveBtnLoading(true);
			const token = await getToken();
			const authHeader = {
				headers: {
					authorization: `Bearer ${token}`,
				},
			};
			const { data: updatedData } = await updateSchemas(
				authHeader,
				activeApplication.id,
				activeMarket,
				value,
				multipleMarketFlag,
				selectedMarkets,
				uiSchemaName,
				reason
			);
			const schemaValue = updatedData.data?.value;
			if (!multipleMarketFlag) {
				setSchemaData((prevData: any) => ({ ...prevData, schemaValue }));
				const newObj: any = {};
				Object.keys(schemaValue || {}).forEach((key) => {
					newObj[key] = schemaValue[key];
				});
				setOriginalValue(newObj);
			} else {
				const { data: schemaData } = await getSchemas(authHeader, activeApplication.id, activeMarket);
				const newObj: any = {};
				Object.keys(schemaData.data?.schemaValue || {}).forEach((key) => {
					newObj[key] = schemaData.data?.schemaValue[key];
				});
				setOriginalValue(newObj);
			}
			setToastMessage("Configuration updated Sucessfully");
		} catch (error: any) {
			setToastMessage(error?.response?.data?.message);
			if (error?.response?.status == 401) {
				setUnsavedChangesValue({
					activeApplicationId: activeApplication.id,
					activeMarket: activeMarket,
					value: JSON.stringify(value),
					uiSchemaName: uiSchemaName,
					userId: currentUser?.user?.id,
					dateTime: new Date().getTime(),
				});
				handleLogout();
			}
		} finally {
			setSaveBtnLoading(false);
		}
	}
};

export const getAllScopes = (uiSchema: any) => {
	const scopes = [] as any;

	function findSchemaScopes(uiSchema: any) {
		if (Object.prototype.hasOwnProperty.call(uiSchema, "scope")) {
			scopes.push(uiSchema["scope"]);
		}
		if (Object.prototype.hasOwnProperty.call(uiSchema, "elements")) {
			for (const key in uiSchema.elements) {
				findSchemaScopes(uiSchema.elements[key]);
			}
		}
	}

	findSchemaScopes(uiSchema);
	return scopes;
};

export const filterElementsByLabel = (obj: any, label: any): any[] => {
	let filtered: any[] = [];

	// Check if object is an array
	if (Array.isArray(obj)) {
		for (let i = 0; i < obj.length; i++) {
			const temp = filterElementsByLabel(obj[i], label);
			filtered.push(...temp); // Add all filtered elements to the result array
		}
	}
	// Check if object is an object
	else if (obj !== null && typeof obj === "object") {
		if (
			(obj.label && obj.label.toLowerCase().includes(label.toLowerCase())) ||
			(obj.subText && obj.subText.toLowerCase().includes(label.toLowerCase()))
		) {
			// Add the object to the filtered array
			filtered.push({ ...obj });
		} else {
			// Recursive call for nested elements
			const children = obj.elements ? filterElementsByLabel(obj.elements, label) : [];
			if (children.length > 0) {
				// Add the parent object if any child matches
				filtered.push({ ...obj, elements: children });
			}
		}
	}
	return filtered;
};

const isPlainObject = (value: unknown): value is Record<string, any> => {
	return value !== null && typeof value === "object" && !Array.isArray(value);
};

const getSchemaAtScope = (schema: JsonSchema | undefined, scope: string): JsonSchema | undefined => {
	if (!schema || !scope || scope === "#") return schema;

	const parts = scope.replace(/^#\//, "").split("/");
	let current: JsonSchema | undefined = schema;

	for (let i = 0; i < parts.length; i++) {
		const part = parts[i];
		if (!current) return undefined;

		if (part === "properties") {
			const key = parts[i + 1];
			current = current.properties?.[key];
			i++;
			continue;
		}

		if (part === "items") {
			current = current.items;
		}
	}

	return current;
};

const joinRelativePropertyScope = (baseScope: string, relativeScope: string): string => {
	const relativePath = relativeScope.replace(/^#\/properties\//, "");
	if (!relativePath) return baseScope;

	return `${baseScope}/properties/${relativePath}`;
};

const collectResolvedScopes = (
	element: UISchemaElement | undefined,
	rootSchema: JsonSchema | undefined,
	currentScope = "#",
	scopes: UISchemaScopeSet = new Set()
): UISchemaScopeSet => {
	if (!element) return scopes;

	let resolvedScope = currentScope;

	if (element.scope) {
		if (currentScope === "#" || element.scope.startsWith(currentScope)) {
			resolvedScope = element.scope;
		} else if (element.scope.startsWith("#/properties/")) {
			resolvedScope = joinRelativePropertyScope(currentScope, element.scope);
		} else {
			resolvedScope = element.scope;
		}

		scopes.add(resolvedScope);
	}

	if (element.elements) {
		element.elements.forEach((child) => {
			collectResolvedScopes(child, rootSchema, resolvedScope, scopes);
		});
	}

	if (element.options?.detail) {
		const schemaAtResolvedScope = getSchemaAtScope(rootSchema, resolvedScope);
		const detailBaseScope = schemaAtResolvedScope?.type === "array" ? `${resolvedScope}/items` : resolvedScope;

		collectResolvedScopes(element.options.detail, rootSchema, detailBaseScope, scopes);
	}

	return scopes;
};

export const buildResolvedUISchemaScopeSet = (
	uiSchema: UISchemaElement | undefined,
	rootSchema: JsonSchema | undefined
): UISchemaScopeSet => {
	return collectResolvedScopes(uiSchema, rootSchema);
};

const isScopeAllowed = (scope: string, allowedScopes?: UISchemaScopeSet): boolean => {
	if (!allowedScopes || allowedScopes.size === 0) return true;
	return allowedScopes.has(scope);
};

const getPropertyScope = (currentScope: string, key: string): string =>
	currentScope === "#" ? `#/properties/${key}` : `${currentScope}/properties/${key}`;

/**
 * Checks whether the user has access to every direct property
 * of an object item inside an array.
 */
const hasAccessToAllArrayItemProperties = (
	schema: JsonSchema,
	currentScope: string,
	allowedScopes?: UISchemaScopeSet
): boolean => {
	if (!schema.properties) {
		return true;
	}

	// No allow-list means all properties are accessible.
	if (!allowedScopes || allowedScopes.size === 0) {
		return true;
	}

	return Object.keys(schema.properties).every((key) => {
		const propertyScope = getPropertyScope(currentScope, key);

		return isScopeAllowed(propertyScope, allowedScopes);
	});
};

export const applyDefaultsToDifference = <T = unknown>(
	diffData: T,
	schema?: JsonSchema,
	allowedScopes?: UISchemaScopeSet,
	currentScope = "#",
	currentData?: unknown,
	isArrayItemObject = false
): T => {
	if (diffData === undefined || !schema) {
		return diffData;
	}

	/**
	 * Handle arrays recursively.
	 */
	if (schema.type === "array") {
		if (!Array.isArray(diffData)) {
			return diffData;
		}

		const currentArray = Array.isArray(currentData) ? currentData : [];

		return diffData.map((item, index) =>
			applyDefaultsToDifference(
				item,
				schema.items,
				allowedScopes,
				`${currentScope}/items`,
				currentArray[index],
				// Only object items in arrays may receive defaults.
				schema.items?.type === "object"
			)
		) as T;
	}

	/**
	 * Only process objects from this point onward.
	 */
	if (schema.type !== "object" || !isPlainObject(diffData)) {
		return diffData;
	}

	const diffObject = diffData as Record<string, unknown>;
	const result: Record<string, unknown> = { ...diffObject };

	const currentObject: Record<string, unknown> = isPlainObject(currentData)
		? (currentData as Record<string, unknown>)
		: {};

	// Process properties that already exist in the diff.
	Object.keys(result).forEach((key) => {
		const propertySchema = schema.properties?.[key];

		if (!propertySchema) {
			return;
		}

		const propertyScope = getPropertyScope(currentScope, key);

		result[key] = applyDefaultsToDifference(
			result[key],
			propertySchema,
			allowedScopes,
			propertyScope,
			currentObject[key],
			// A nested object is not itself an array item.
			// The array branch will set this to true for object items.
			false
		);
	});

	/**
	 * Defaults are applied only when:
	 *
	 * 1. The current object is a direct object item in an array.
	 * 2. The array item schema has properties.
	 * 3. The user has access to every property in the array item schema.
	 */
	const canApplyArrayItemDefaults =
		isArrayItemObject && !!schema.properties && hasAccessToAllArrayItemProperties(schema, currentScope, allowedScopes);

	if (!canApplyArrayItemDefaults) {
		return result as T;
	}

	/**
	 * Apply defaults to missing direct properties.
	 */
	Object.keys(schema.properties ?? {}).forEach((key) => {
		const propertySchema = schema.properties?.[key];

		if (!propertySchema) {
			return;
		}

		// false, 0, "", and null are valid values.
		// Only undefined means that the value is missing.
		const missingFromDiff = result[key] === undefined;
		const missingFromCurrentData = currentObject[key] === undefined;

		if (missingFromDiff && missingFromCurrentData && propertySchema.default !== undefined) {
			result[key] = cloneDeep(propertySchema.default);
		}
	});

	return result as T;
};
